import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Chevron, RowIcon, TONE_CHIP, TONE_TEXT, type IconName, type Tone } from "@/components/home/tokens";

/** Compact layout primitives for Home.
 *
 *  Density is the point: every role's page should land inside one screen, so
 *  these run tighter than the app's general cards — 10px labels, 2xl numbers,
 *  ~14px padding. `Panel` exists so every block shares one header treatment;
 *  the previous version stacked hand-built cards and the section titles, paddings and
 *  baselines never lined up.
 */

/** A titled card. `right` is the inline meta slot (a count, a rate, a link). */
export function Panel({
  title,
  right,
  children,
  className = "",
  bodyClass = "",
  flush,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClass?: string;
  /** Body goes edge-to-edge — for lists that supply their own row padding. */
  flush?: boolean;
}) {
  return (
    <div className={`card flex flex-col min-w-0 ${className}`}>
      {title && (
        <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">
            {title}
          </span>
          {right && <span className="text-[11px] text-slate-500 tabular-nums flex-shrink-0">{right}</span>}
        </div>
      )}
      <div className={`${flush ? "" : "px-4 pb-3.5"} ${!title && !flush ? "pt-3.5" : ""} flex-1 min-w-0 ${bodyClass}`}>
        {children}
      </div>
    </div>
  );
}

/** One KPI. Optional thin bar and a foot line, both muted. */
export function Stat({
  label,
  value,
  suffix,
  foot,
  bar,
  tone = "default",
  to,
  size = "lg",
}: {
  label: string;
  value: ReactNode;
  suffix?: string;
  foot?: string;
  bar?: number;
  tone?: "default" | "emerald" | "amber" | "rose";
  to?: string;
  /** ``md`` for values that carry two figures side by side and would
   *  otherwise overflow a quarter-width tile. */
  size?: "lg" | "md";
}) {
  const valueSize = size === "md" ? "text-lg" : "text-2xl";
  const pct = bar == null ? null : Math.max(0, Math.min(1, bar));
  // Only draw the track when there is a filled portion to show. At zero the
  // number already says so, and an all-grey line just reads as chrome.
  const showBar = pct != null && pct > 0;
  const valueTone =
    tone === "emerald" ? "text-emerald-300"
    : tone === "amber" ? "text-amber-300"
    : tone === "rose" ? "text-rose-300"
    : "text-slate-100";
  const barTone =
    tone === "emerald" ? "bg-emerald-500"
    : tone === "amber" ? "bg-amber-500"
    : tone === "rose" ? "bg-rose-500"
    : "bg-accent-500";

  const inner = (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5 min-w-0">
        <span className={`font-semibold tabular-nums leading-none truncate ${valueSize} ${valueTone}`}>
          {value}
        </span>
        {suffix && (
          <span className="text-[11.5px] text-slate-500 tabular-nums flex-shrink-0">{suffix}</span>
        )}
      </div>
      {/* The slot is always reserved so labels stay on one baseline across a
          KPI row, but the grey track is only drawn when there is actually a
          value to plot — an empty track on a tile with nothing to measure is
          just a decoration that reads as "0%". */}
      <div
        className={`mt-2 h-[3px] rounded-full overflow-hidden ${
          showBar ? "bg-white/[0.06]" : ""
        }`}
      >
        {showBar && (
          <div className={`h-full rounded-full ${barTone}`} style={{ width: `${pct! * 100}%` }} />
        )}
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500 truncate min-h-[15px]">
        {foot ?? "\u00a0"}
      </div>
    </>
  );

  // Top-aligned, not centred: cards in a KPI row stretch to a common height,
  // so centring makes each label float to a different y depending on how much
  // content that particular tile happens to carry.
  const cls = "card px-3.5 py-3 flex flex-col justify-start min-w-0";
  return to ? (
    <Link to={to} className={`${cls} hover:border-white/15 transition`}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

/** A dense attention row: small tinted glyph, count, label, muted hint.
 *  Replaces the old tall NeedCard, which spent a 9×9 chip and three stacked
 *  blocks on what is really one line of information. */
export function NeedRow({
  icon,
  tone,
  count,
  label,
  hint,
  to,
}: {
  icon: IconName;
  tone: Tone;
  count: number;
  label: string;
  hint?: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-white/[0.02] transition min-w-0"
    >
      <span
        className={`flex items-center justify-center h-7 w-7 rounded-lg flex-shrink-0 ${TONE_CHIP[tone]}`}
      >
        <RowIcon name={icon} size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className={`text-[15px] font-semibold tabular-nums leading-none ${TONE_TEXT[tone]}`}>
            {count.toLocaleString()}
          </span>
          <span className="text-[12.5px] text-slate-200 truncate">{label}</span>
        </span>
        {hint && <span className="block text-[10.5px] text-slate-500 mt-0.5 truncate">{hint}</span>}
      </span>
      <Chevron size={13} />
    </Link>
  );
}

/** A capped, scrollable list that fades its bottom edge **only when there is
 *  more below**.
 *
 *  Without it a long list clips its last row mid-height and reads as a
 *  rendering bug; with an unconditional fade, a short list that fits would get
 *  a mystery gradient on its final row. So the fade is driven by actual
 *  overflow, remeasured on resize and whenever the content changes.
 */
export function ScrollList({
  children,
  max = 320,
  deps,
}: {
  children: ReactNode;
  max?: number;
  /** Changes to this remeasure — pass the row count. */
  deps?: unknown;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, deps]);

  return (
    <div
      ref={ref}
      onScroll={measure}
      className="divide-y divide-white/[0.04] overflow-y-auto scrollbar-thin"
      style={{
        maxHeight: max,
        maskImage: overflowing
          ? "linear-gradient(to bottom, #000 calc(100% - 18px), transparent)"
          : undefined,
        WebkitMaskImage: overflowing
          ? "linear-gradient(to bottom, #000 calc(100% - 18px), transparent)"
          : undefined,
      }}
    >
      {children}
    </div>
  );
}


/** Empty state sized to sit inside a Panel without inflating it. */
export function Quiet({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-3.5 py-5 text-center">
      <div className="text-[12.5px] text-slate-300">{title}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
